import { isEmpty, result, noop } from 'underscore';
import Promise from 'bluebird';
import { assign } from 'xstate';
import { log } from '@bingads-webui-campaign-react/step-wizard-machine';
import { logRecommendations } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { logCopilotRecommendations } from '@bingads-webui-campaign-react/copilot-common';
import { getErrorsPlaceholders, loadAssetGroup, saveAssetGroupAndLinkedData, viewModelToModelForAssetGroup } from '../../model/asset-group';
import { loadCampaign, modelToViewModelForCampaign, viewModelToModelForCampaign } from '../../model/campaign';
import { loadAdExtensionData } from '../../model/ad-extension';
import { ScenarioName } from '../../common/constants';

const hasCampaignData = context =>
  Boolean(result(context, ['stepsData', 'campaign', 'Id']));

const getCampaignId = context =>
  result(context, ['stepsData', 'campaign', 'Id']) || // when coming from the Campaign settings step
  // result(context, ['data', 'campaign', 'data', 'Id']) || // when in the Asset group creation and settings flows and already loaded and cached the campaign's data
  result(context, ['stepsData', 'campaignId']); // when in the Asset group creation and settings flows

const getAssetGroupId = context =>
  result(context, ['data', 'assetGroup', 'data', 'Id']) || // when in the Campaign creation flow
  result(context, ['stepsData', 'assetGroup', 'Id']) || // when in the Asset group creation or settings flows
  result(context, ['stepsData', 'assetGroupId']); // when in the Asset group settings flows

// FIXME: [User Story 4721721: PMax wizard perceived performance optimizations](https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/4721721)
// Always reloads the asset group. Optimization can be done at the EDM level
export const needsLoading = (/* context */) => true;

/**
 * Loads step data asynchronously. The returned promise must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 *
 * The function is called when the wizard enters the step.
 * The step will be blocked until the promise resolves.
 * @param {Object} context - the current context
 * @returns {Promise<Object>} a promise that must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 */
export const loadData = (context) => {
  const {
    cid, aid, permissions, scenarioName,
  } = context.stepsData;
  const campaignId = getCampaignId(context);
  if (!campaignId) {
    throw new Error('Asset group settings/creation page requires a campaign ID');
  }
  const assetGroupId = getAssetGroupId(context);
  if (context.stepsData.isEditing && !assetGroupId) {
    throw new Error('Asset group settings page requires an asset group ID');
  }
  const {
    PmaxExtensionPreview,
    PMaxNewAspectRatios: isPMaxNewAspectRatiosEnabled,
    PMaxExcludeNewAspectRatios: isPMaxNewAspectRatiosExcluded,
  } = permissions.dynamic;
  const isLoadExtensionFromAPI = PmaxExtensionPreview && (scenarioName === ScenarioName.PerformanceMaxAssetGroupCreation || scenarioName === ScenarioName.PerformanceMaxAssetGroupSettings);

  const isSearchThemeEnabled = permissions && permissions.dynamic && permissions.dynamic.SearchThemes;
  const isUrlRulesEnabled = permissions && permissions.dynamic && permissions.dynamic.AssetGroupUrlRules;
  const isUrlRulesV2Enabled = permissions && permissions.dynamic && permissions.dynamic.AssetGroupUrlRulesV2;
  const isPMaxNewAspectRatios = isPMaxNewAspectRatiosEnabled && !isPMaxNewAspectRatiosExcluded;

  if (isPMaxNewAspectRatios) {
    log('[AssetGroupSetting] - New Aspect Ratios is enabled');
  }

  const loadPromises = {
    ...assetGroupId ? { assetGroup: loadAssetGroup(cid, aid, campaignId, assetGroupId, context.stepsData.i18n, isSearchThemeEnabled, isUrlRulesEnabled, isUrlRulesV2Enabled) } : {},
    // Full campaign entity is needed and will be loaded, if it's not loaded yet
    // and will be used not from `data` but from `stepsData` to simplify the "get-campaign-data" logic
    ...hasCampaignData(context) ? {} : {
      campaign: loadCampaign(cid, aid, campaignId)
        .then(campaignModel => modelToViewModelForCampaign({ campaignModel })),
    },
    ...isLoadExtensionFromAPI ? { extensionsAssociations: loadAdExtensionData(cid, aid, campaignId) } : {},
  };

  return Promise.props(loadPromises).then((data) => {
    log('Asset step data loaded, data=_', data);
    return {
      assetGroup: context.data.assetGroup,
      originalAssetGroup: { ...context.data.assetGroup, ...data.assetGroup },
      extensionsAssociations: context.data.extensionsAssociations,
      recommendationContext: context.data.recommendationContext,
      ...getErrorsPlaceholders(),
      ...data,
    };
  });
};

const logRecommendationsFromContext = (context, assetGroup, originalAssetGroup) => {
  const {
    isEditing, aid, uid, campaign: { Id: campaignId },
  } = context.stepsData;
  const { activity, recommendationContext } = context.data;
  logRecommendations({
    recommendationContext,
    activity,
    aid,
    uid,
    campaignId,
    isEditing,
    assetGroup,
    originalAssetGroup,
  });

  logCopilotRecommendations({
    recommendationContext,
    assetGroup,
    isEditing,
  });
};

export const saveData = (context) => {
  const assetGroupChanges = {
    assetGroup: context.data.assetGroup,
    originalAssetGroup: context.data.originalAssetGroup,
    campaign: context.stepsData.campaign,
  };

  const saveContext = {
    scenario: context.stepsData.scenarioName,
    activity: context.data.activity || { trace: noop },
    activityLogger: context.stepsData.scenario.createActivity('saveAssetGroupAndLinkedData'),
    i18n: context.stepsData.i18n,
    serverContext: context.stepsData.serverContext,
    cid: context.stepsData.cid,
    aid: context.stepsData.aid,
  };

  const { permissions } = context.stepsData;
  const {
    PMaxNewAspectRatios: isPMaxNewAspectRatiosEnabled,
    PMaxExcludeNewAspectRatios: isPMaxNewAspectRatiosExcluded,
  } = permissions.dynamic;
  const isPMaxNewAspectRatios = isPMaxNewAspectRatiosEnabled && !isPMaxNewAspectRatiosExcluded;

  if (isPMaxNewAspectRatios) {
    saveContext.activity.trace('[AssetGroupSetting] - New Aspect Ratios is enabled');
  }

  return saveAssetGroupAndLinkedData(assetGroupChanges, saveContext)
    .then((data) => {
      saveContext.activity.trace(`[PmaxWizard] saveData succeeded. StepId: ${context.id}`);
      logRecommendationsFromContext(context, viewModelToModelForAssetGroup(data.assetGroup), context.data.originalAssetGroup ? viewModelToModelForAssetGroup(context.data.originalAssetGroup) : {});
      return data;
    }).then(data => ({
      ...data,
      extensionsAssociations: context.data.extensionsAssociations,
      recommendationContext: context.data.recommendationContext,
    })).catch((errorData) => {
      saveContext.activity.trace(`[PmaxWizard] saveData failed. StepId: ${context.id}`);

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        ...errorData,
        extensionsAssociations: context.data.extensionsAssociations,
        recommendationContext: context.data.recommendationContext,
      });
    });
};

export const clearErrors = assign({
  data: context => ({
    ...context.data,
    ...getErrorsPlaceholders(),
  }),
});

export const isValid = context => context.data.assetGroup.isValid;
export const isDirty = context => context.data.assetGroup.isDirty;
const hasServerErrors = context => !isEmpty(context.data.serverErrors) || !isEmpty(context.data.systemErrors);
export const needsSaving = context => isDirty(context) || hasServerErrors(context);

export const patchStepsData = assign({
  stepsData: (context/* , event */) => ({
    ...context.stepsData,
    assetGroup: viewModelToModelForAssetGroup(context.data.assetGroup),
    // Full campaign entity is needed and will be loaded, if it's not loaded yet
    // and will be used not from `data` but from `stepsData` to simplify the "get-campaign-data" logic
    ...hasCampaignData(context) ? {} :
      { campaign: viewModelToModelForCampaign(context.data.campaign) },
  }),
});
